// ============================================================================
// ScopedLimitsPanel — usage-vs-limit percentages for the selected scope (R19)
//
// Complements UsageLimitMeters (the viewer's OWN limits): this panel shows how
// the SELECTED member or team is tracking against the limits an admin set —
// each meter is % of limit used, colored by the same severity ladder the
// enforcement gate uses, so this page and the block never disagree.
//
//  • member scope → every limit window on that member, full meters
//  • role scope   → the role's configured limits + a per-member utilization
//                   list for everyone in that team (role + roles beneath it)
//  • all scope    → the members closest to their limits, workspace/teams-wide
//
// Renders ONLY when the viewer could actually load limit data (usage.limits.view
// or manage — the parent passes `statuses: null` otherwise), so a viewer with
// activity visibility but no limit visibility simply never sees this panel.
// ============================================================================

import React, { useMemo } from 'react';
import styled from 'styled-components';
import type {
  ActivityScopeMember,
  ActivityScopeRole,
  MemberLimitStatus,
  MemberLimitWindowStatus,
  RoleLimitSummary,
  UsageLimitWindow,
} from '../../services/rbacService';
import type { ActivityScopeValue } from './ActivityScopeSelector';
import { humanizeScopeLabel, memberLabel } from './ActivityScopeSelector';
import {
  ACTIVITY_COLORS,
  CARD_STYLE,
  formatResetTime,
  formatUsd,
  severityColor,
  severityLabel,
  SeverityLevel,
} from './activityTheme';

const Panel = styled.section`
  ${CARD_STYLE}
  padding: 1.25rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${ACTIVITY_COLORS.ink};
  margin: 0 0 0.25rem;
  text-transform: lowercase;
`;

const Note = styled.p`
  color: ${ACTIVITY_COLORS.inkSecondary};
  font-size: 0.8125rem;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const MeterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.875rem;
`;

const MeterCard = styled.div`
  border: 1px solid ${ACTIVITY_COLORS.gridline};
  border-radius: 8px;
  padding: 0.75rem 0.875rem;
`;

const MeterHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
`;

const MeterName = styled.span`
  color: ${ACTIVITY_COLORS.ink};
  font-weight: 500;
  text-transform: lowercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MeterPct = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  flex: none;
`;

const Track = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${ACTIVITY_COLORS.gridline};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(p) => Math.max(p.$pct > 0 ? 2 : 0, Math.min(100, p.$pct))}%;
  background: ${(p) => p.$color};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const MeterFoot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.4375rem;
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  font-variant-numeric: tabular-nums;
  flex-wrap: wrap;
`;

const ConfigList = styled.ul`
  margin: 0 0 1rem;
  padding: 0;
  list-style: none;
  font-size: 0.8125rem;
  color: ${ACTIVITY_COLORS.ink};

  li {
    padding: 0.25rem 0;
    color: ${ACTIVITY_COLORS.inkSecondary};

    b {
      color: ${ACTIVITY_COLORS.ink};
      font-weight: 500;
    }
  }
`;

const EmptyNote = styled.div`
  color: ${ACTIVITY_COLORS.inkMuted};
  font-size: 0.8125rem;
  padding: 0.5rem 0;
`;

function windowConfigText(w: UsageLimitWindow): string {
  // Token-metered caps never show raw token numbers (2026-08-13 WU mandate) —
  // the config line names the cap; the meters below carry the % utilization.
  // Display only: the configured limit itself is untouched.
  const amounts = [
    typeof w.costUsdLimit === 'number' ? formatUsd(w.costUsdLimit) : null,
    typeof w.tokenLimit === 'number' ? 'a usage cap' : null,
  ]
    .filter(Boolean)
    .join(' or ');
  const span =
    w.kind === 'rolling'
      ? `per rolling ${w.amount ?? '?'} ${w.unit ?? 'hours'}`
      : w.kind === 'calendar-day'
        ? 'per day'
        : w.kind === 'calendar-week'
          ? 'per week'
          : 'per month';
  return `${amounts || 'no cap'} ${span}`;
}

function usedOfLimitText(w: MemberLimitWindowStatus): string {
  // $-caps show dollars; token-metered windows render as % of the window —
  // raw token counts never display (2026-08-13 WU mandate). Display only.
  return w.metric === 'usd'
    ? `${formatUsd(w.used)} of ${formatUsd(w.limit)}`
    : `${Math.min(100, Math.round((w.ratio ?? 0) * 100))}% of usage window used`;
}

/** The window closest to its cap — what "how close are they" means. */
function bindingWindow(s: MemberLimitStatus): MemberLimitWindowStatus | null {
  if (!s.windows || s.windows.length === 0) return null;
  return s.windows.reduce((a, b) => (b.ratio > a.ratio ? b : a));
}

const WindowMeter: React.FC<{ name: string; window: MemberLimitWindowStatus; nowMs: number }> = ({
  name,
  window: w,
  nowMs,
}) => {
  const level = (w.level ?? 'none') as SeverityLevel;
  const emphasize = level !== 'none';
  const color = severityColor(level);
  const pct = Math.min(100, Math.round((w.ratio ?? 0) * 100));
  return (
    <MeterCard>
      <MeterHead>
        <MeterName>{name}</MeterName>
        <MeterPct $color={emphasize ? color : ACTIVITY_COLORS.ink}>{pct}%</MeterPct>
      </MeterHead>
      <Track
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${name}: ${pct}% of limit used`}
      >
        <Fill $pct={pct} $color={color} />
      </Track>
      <MeterFoot>
        <span>{usedOfLimitText(w)}</span>
        <span>{severityLabel(level)}</span>
      </MeterFoot>
      <MeterFoot>
        <span>{w.label}</span>
        <span>refreshes {formatResetTime(w.resetMs, nowMs)}</span>
      </MeterFoot>
    </MeterCard>
  );
};

interface Props {
  scope: ActivityScopeValue;
  roles: ActivityScopeRole[];
  members: ActivityScopeMember[];
  /** null = viewer cannot see limit data — the panel stays absent */
  statuses: MemberLimitStatus[] | null;
  roleLimits: RoleLimitSummary[] | null;
}

const ScopedLimitsPanel: React.FC<Props> = ({ scope, roles, members, statuses, roleLimits }) => {
  const nowMs = Date.now();

  // The status endpoint is workspace-wide; this page may not be. Everything
  // rendered here stays inside the members the scope options declared visible
  // — a subtree viewer never sees an out-of-subtree name in the ranking.
  const visibleIds = useMemo(() => new Set(members.map((m) => m.memberId)), [members]);

  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) map.set(m.memberId, memberLabel(m));
    return (memberId: string) => map.get(memberId) || memberId.slice(0, 12);
  }, [members]);

  // Members inside a role scope: the role plus every visible role beneath it.
  const scopeMemberIds = useMemo(() => {
    if (scope.kind !== 'role') return null;
    const childrenOf = new Map<string, string[]>();
    for (const role of roles) {
      for (const parentId of role.parentRoleIds) {
        const list = childrenOf.get(parentId) ?? [];
        list.push(role.roleId);
        childrenOf.set(parentId, list);
      }
    }
    const inScope = new Set<string>();
    const queue = [scope.roleId];
    while (queue.length > 0) {
      const id = queue.pop()!;
      if (inScope.has(id)) continue;
      inScope.add(id);
      for (const child of childrenOf.get(id) ?? []) queue.push(child);
    }
    const ids = new Set<string>();
    for (const m of members) {
      if (m.roleIds.some((id) => inScope.has(id))) ids.add(m.memberId);
    }
    return ids;
  }, [scope, roles, members]);

  if (statuses === null) return null;

  const statusOf = new Map(statuses.map((s) => [s.userId, s]));

  if (scope.kind === 'member') {
    const status = statusOf.get(scope.memberId);
    const name = nameOf(scope.memberId);
    return (
      <Panel>
        <Title>usage limits — {name}</Title>
        {status && status.windows.length > 0 ? (
          <>
            <Note>how {name} is tracking against each limit set for them.</Note>
            <MeterGrid>
              {status.windows.map((w, idx) => (
                <WindowMeter key={`${w.label}-${idx}`} name={w.label} window={w} nowMs={nowMs} />
              ))}
            </MeterGrid>
          </>
        ) : (
          <EmptyNote>no usage limits are set for this member.</EmptyNote>
        )}
      </Panel>
    );
  }

  if (scope.kind === 'role') {
    const role = roles.find((r) => r.roleId === scope.roleId);
    const config = roleLimits?.find((r) => r.roleId === scope.roleId);
    const windows = config?.usageLimits?.windows ?? [];
    const teamStatuses = statuses.filter(
      (s) => visibleIds.has(s.userId) && scopeMemberIds?.has(s.userId)
    );
    if (windows.length === 0 && teamStatuses.length === 0) {
      return (
        <Panel>
          <Title>usage limits — {role ? humanizeScopeLabel(role.name) : 'team'}</Title>
          <EmptyNote>no usage limits are configured for this team.</EmptyNote>
        </Panel>
      );
    }
    return (
      <Panel>
        <Title>usage limits — {role ? humanizeScopeLabel(role.name) : 'team'}</Title>
        {windows.length > 0 && (
          <ConfigList>
            {windows.map((w) => (
              <li key={w.id}>
                <b>{w.label || 'limit'}</b> — {windowConfigText(w)}, applied to each member holding
                this role
              </li>
            ))}
          </ConfigList>
        )}
        {teamStatuses.length > 0 ? (
          <>
            <Note>how each member of this team is tracking against their binding limit.</Note>
            <MeterGrid>
              {teamStatuses
                .map((s) => ({ s, w: bindingWindow(s) }))
                .filter((x): x is { s: MemberLimitStatus; w: MemberLimitWindowStatus } => !!x.w)
                .sort((a, b) => b.w.ratio - a.w.ratio)
                .map(({ s, w }) => (
                  <WindowMeter
                    key={s.userId}
                    name={s.displayName || s.email || nameOf(s.userId)}
                    window={w}
                    nowMs={nowMs}
                  />
                ))}
            </MeterGrid>
          </>
        ) : (
          windows.length > 0 && (
            <EmptyNote>nobody in this team has usage counted against these limits yet.</EmptyNote>
          )
        )}
      </Panel>
    );
  }

  // Whole-workspace / your-teams view: surface whoever is closest to a limit.
  const ranked = statuses
    .filter((s) => visibleIds.has(s.userId))
    .map((s) => ({ s, w: bindingWindow(s) }))
    .filter((x): x is { s: MemberLimitStatus; w: MemberLimitWindowStatus } => !!x.w)
    .sort((a, b) => b.w.ratio - a.w.ratio)
    .slice(0, 8);
  if (ranked.length === 0) return null;
  return (
    <Panel>
      <Title>limit utilization</Title>
      <Note>members closest to their usage limits, most utilized first.</Note>
      <MeterGrid>
        {ranked.map(({ s, w }) => (
          <WindowMeter
            key={s.userId}
            name={s.displayName || s.email || nameOf(s.userId)}
            window={w}
            nowMs={nowMs}
          />
        ))}
      </MeterGrid>
    </Panel>
  );
};

export default ScopedLimitsPanel;
